import { Box, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React from 'react';

interface InfoRowProps {
  label: string;
  value: string | null;
  icon: React.ReactNode;
  maxLength?: number;
}

const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  icon,
  maxLength = 40,
}) => {
  const hasValue = value && value.trim() !== '';
  const displayValue = hasValue ? value : 'No information available';
  const textToShow =
    hasValue && maxLength
      ? value.length > maxLength
        ? value.slice(0, maxLength) + '...'
        : value
      : displayValue;

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
      <Box
        sx={{
          backgroundColor: hasValue
            ? 'rgba(25, 118, 210, 0.1)'
            : 'rgba(158, 158, 158, 0.1)',
          borderRadius: '50%',
          p: 0.4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '24px',
          height: '24px',
        }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          sx: {
            ...(icon as React.ReactElement).props.sx,
            color: hasValue
              ? (icon as React.ReactElement).props.sx?.color || 'primary.main'
              : 'text.disabled',
          },
        })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem', display: 'block', mb: 0.2 }}
        >
          {label}
        </Typography>
        {hasValue ? (
          <LightTooltip title={<Typography padding={1}>{value}</Typography>}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              {textToShow}
            </Typography>
          </LightTooltip>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1.2,
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            {displayValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InfoRow;
